import { eq } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { useLocalSearchParams } from 'expo-router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Pressable, SectionList, StyleSheet, useWindowDimensions, View } from 'react-native'
import { Dialog, Icon, IconButton, SegmentedButtons, Text, useTheme } from 'react-native-paper'
import { DEFAULT_PERIOD_TYPE, DEFAULT_WEEK_START_DAY } from '@/constants/periods'
import { useSemanticColors } from '@/constants/SemanticColors'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { useExportXLSX } from '@/hooks/useExportXLSX'
import { useActiveEntriesBeforeDate, useLockPeriod } from '@/hooks/useLockPeriod'
import { useOrgSettings } from '@/hooks/useOrgSettings'
import { useOrgUserIds } from '@/hooks/useOrgUserIds'
import { usePeriodLocked } from '@/hooks/usePeriodLocked'
import { usePeriodNavigation } from '@/hooks/usePeriodNavigation'
import { useRejectDialog } from '@/hooks/useRejectDialog'
import { useReportingData } from '@/hooks/useReportingData'
import { useAuth } from '@/lib/auth'
import { BREAKPOINTS } from '@/lib/config'
import { calculateDuration, formatHoursReadable, formatTimeOfDay, getISODate } from '@/lib/dates'
import { sumWorkHours } from '@/lib/group-entries-by-date'
import {
    type DaySection,
    type DayUserGroup,
    groupEntriesByDayThenUser,
} from '@/lib/group-entries-by-day-user'
import {
    groupEntriesByUserThenDay,
    type UserDayGroup,
    type UserSection,
} from '@/lib/group-entries-by-user-day'
import { getPeriodBounds } from '@/lib/periods'
import { useStore } from '@/lib/pocketbase'
import {
    type GroupingMode,
    useApprovalDayExpansion,
    useApprovalGroupingMode,
    useApprovalResetExpansion,
    useApprovalUserExpansion,
} from '@/stores/approval-store'
import { useConsolidationStore } from '@/stores/consolidation-store'
import type { PeriodConfig, WeekStartDay } from '@/types/periods'
import type { Tag } from '@/types/tags'
import type { TimeEntriesExpanded } from '@/types/timeTracking'
import { Button } from '@/ui/Button'
import { ListWrapper } from '@/ui/ListWrapper'
import { LoadingIndicator } from '@/ui/LoadingIndicator'
import { PeriodNavigator } from '@/ui/PeriodNavigator'
import { DailyActivityCard } from '@/ui/time-entry/DailyActivityCard'
import { ManualEntryDialog } from '@/ui/time-entry/ManualEntryDialog'
import { PeriodTotal } from '@/ui/time-entry/PeriodTotal'
import { RejectDialog } from '@/ui/time-entry/RejectDialog'
import { TravelMapModal } from '@/ui/time-entry/TravelMapModal'

export default function ReportingPage() {
    useAuth()
    const params = useLocalSearchParams<{ orgSlug: string }>()
    const theme = useTheme()
    const { org, role } = useCurrentOrg()
    const isAdmin = role === 'admin' || role === 'clerical'
    const [tagsCollection] = useStore('tags')

    const { data: orgTags = [] } = useLiveQuery(
        q =>
            org?.id
                ? q.from({ tags: tagsCollection }).where(({ tags }) => eq(tags.org, org.id))
                : undefined,
        [org?.id]
    )

    const resolveTagNames = useMemo(() => {
        const map = new Map<string, string>()
        for (const tag of orgTags as Tag[]) {
            map.set(tag.id, tag.name)
        }
        return (tagIds: string[] | undefined) => {
            if (!tagIds?.length) return ''
            return tagIds
                .map(id => map.get(id))
                .filter((n): n is string => Boolean(n))
                .join(', ')
        }
    }, [orgTags])

    const { settings, isLoading: settingsLoading } = useOrgSettings(params.orgSlug)
    const periodConfig: PeriodConfig = {
        periodType: settings?.period_type || DEFAULT_PERIOD_TYPE,
        weekStartDay: (settings?.week_start_day ?? DEFAULT_WEEK_START_DAY) as WeekStartDay,
    }
    const { periodDate, handlePrevious, handleNext, handleDatePicked } =
        usePeriodNavigation(periodConfig)

    const { start: periodStart, end: periodEnd } = getPeriodBounds(periodDate, periodConfig)
    const entriesLockedUntil =
        (settings as unknown as { entries_locked_until?: string } | null)?.entries_locked_until ||
        null
    const { isPeriodLocked } = usePeriodLocked({
        entriesLockedUntil,
        periodStart,
        periodEnd,
    })

    const { lockPeriod, unlockPeriod, bulkCloseAndLock, isLocking, isUnlocking } =
        useLockPeriod(settings)
    const activeConflicts = useActiveEntriesBeforeDate(
        settings?.id,
        isPeriodLocked ? null : periodEnd
    )
    const [showLockConfirm, setShowLockConfirm] = useState(false)

    const handleLockPeriod = useCallback(() => {
        if (activeConflicts.length > 0) {
            setShowLockConfirm(true)
        } else {
            lockPeriod({ lockDate: periodEnd })
        }
    }, [activeConflicts, lockPeriod, periodEnd])

    const handleConfirmBulkCloseAndLock = useCallback(() => {
        bulkCloseAndLock({
            lockDate: periodEnd,
            activeEntryIds: activeConflicts.map(c => c.entryId),
        })
        setShowLockConfirm(false)
    }, [bulkCloseAndLock, periodEnd, activeConflicts])

    const { userColorMap } = useOrgUserIds()
    const reportingData = useReportingData(periodDate, periodConfig, !settingsLoading)

    const allReviewed = useMemo(() => {
        if (!reportingData.hasEntries) return false
        const allEntries = Object.values(reportingData.entriesByUser).flat()
        return allEntries.every(e => e.status === 'approved' || e.status === 'rejected')
    }, [reportingData.entriesByUser, reportingData.hasEntries])

    const shouldOfferLock = allReviewed && !isPeriodLocked
    const rejectDialog = useRejectDialog()
    const { exportToXLSX, isExporting } = useExportXLSX({
        periodLabel: reportingData.periodLabel,
        entriesByUser: reportingData.entriesByUser,
        resolveTagNames,
    })
    const { groupingMode, setGroupingMode } = useApprovalGroupingMode()
    const { expandedDayKeys, toggleDayKey } = useApprovalDayExpansion()
    const { expandedUserKeys, toggleUserKey } = useApprovalUserExpansion()
    const resetExpansion = useApprovalResetExpansion()
    const [editingEntry, setEditingEntry] = useState<TimeEntriesExpanded | null>(null)
    const [addingForUser, setAddingForUser] = useState<{
        userId: string
        userName: string
    } | null>(null)
    const [travelEntry, setTravelEntry] = useState<TimeEntriesExpanded | null>(null)
    const [consolidationData, setConsolidationData] = useState<{
        clockIn: Date
        clockOut: Date
        jobId: string
        locationId: string
        userId: string
        userName: string
        entriesToDelete: string[]
        consolidationNote: string
        originalTotalSeconds: number
    } | null>(null)
    const clearAllSelections = useConsolidationStore(state => state.clearAll)

    // biome-ignore lint/correctness/useExhaustiveDependencies: clear selections when period changes
    useEffect(() => {
        clearAllSelections()
        resetExpansion()
    }, [periodDate, clearAllSelections, resetExpansion])

    const daySections = useMemo(
        () =>
            groupingMode === 'day'
                ? groupEntriesByDayThenUser(reportingData.entriesByUser, userColorMap).map(
                      section => ({
                          ...section,
                          data: expandedDayKeys[section.dateKey] ? section.data : [],
                      })
                  )
                : [],
        [groupingMode, reportingData.entriesByUser, userColorMap, expandedDayKeys]
    )

    const userSections = useMemo(
        () =>
            groupingMode === 'user'
                ? groupEntriesByUserThenDay(reportingData.entriesByUser, userColorMap).map(
                      section => ({
                          ...section,
                          data: expandedUserKeys[section.userId] ? section.data : [],
                      })
                  )
                : [],
        [groupingMode, reportingData.entriesByUser, userColorMap, expandedUserKeys]
    )

    const periodTotalHours = useMemo(
        () => sumWorkHours(Object.values(reportingData.entriesByUser).flat()),
        [reportingData.entriesByUser]
    )

    const handleEditEntry = useCallback((entry: TimeEntriesExpanded) => {
        setEditingEntry(entry)
    }, [])

    const handleAddEntry = useCallback((userId: string, userName: string) => {
        setAddingForUser({ userId, userName })
    }, [])

    const handleConsolidate = useCallback(
        (groupKey: string) => {
            const [userId, dateKey] = groupKey.split(':')
            const selections = useConsolidationStore.getState().selections[groupKey]
            if (!selections || selections.size < 2) return

            const userEntries = reportingData.entriesByUser[userId] ?? []
            const dayEntries = userEntries.filter(e => getISODate(e.clock_in) === dateKey)
            const checked = dayEntries
                .filter(e => selections.has(e.id))
                .sort((a, b) => new Date(a.clock_in).getTime() - new Date(b.clock_in).getTime())
            if (checked.length < 2) return

            const locations = new Set(checked.map(e => e.location).filter(Boolean))
            const entryLines = checked.map(e => {
                const clockIn = formatTimeOfDay(e.clock_in)
                const clockOut = e.clock_out ? formatTimeOfDay(e.clock_out) : '…'
                const note = e.notes ? ` (${e.notes})` : ''
                return `• ${clockIn} → ${clockOut}${note}`
            })
            const consolidationNote = `Merged from ${checked.length} entries:\n${entryLines.join('\n')}`
            const originalTotalSeconds = checked.reduce((sum, e) => sum + calculateDuration(e), 0)
            setConsolidationData({
                clockIn: new Date(checked[0].clock_in),
                clockOut: new Date(
                    checked[checked.length - 1].clock_out || checked[checked.length - 1].clock_in
                ),
                jobId: checked[0].job,
                locationId: locations.size === 1 ? checked[0].location : '',
                userId,
                userName: checked[0].expand?.user?.name || 'Unknown User',
                entriesToDelete: checked.map(e => e.id),
                consolidationNote,
                originalTotalSeconds,
            })
        },
        [reportingData.entriesByUser]
    )

    const renderDayItem = useCallback(
        ({ item }: { item: DayUserGroup }) => (
            <DailyActivityCard
                dayGroup={item.dayGroup}
                onOpenRejectDialog={rejectDialog.openDialog}
                onEditEntry={handleEditEntry}
                onTravelTap={setTravelEntry}
                isAdmin={isAdmin}
                showApprove={true}
                onAddEntry={isAdmin ? () => handleAddEntry(item.userId, item.userName) : undefined}
                userName={item.userName}
                userColor={item.userColor}
                showConsolidate={isAdmin}
                onConsolidate={handleConsolidate}
            />
        ),
        [rejectDialog.openDialog, handleEditEntry, isAdmin, handleAddEntry, handleConsolidate]
    )

    const renderDaySectionHeader = useCallback(
        ({ section }: { section: DaySection }) => (
            <DaySectionHeader
                section={section}
                expanded={!!expandedDayKeys[section.dateKey]}
                onToggle={() => toggleDayKey(section.dateKey)}
            />
        ),
        [expandedDayKeys, toggleDayKey]
    )

    const renderUserItem = useCallback(
        ({ item, section }: { item: UserDayGroup; section: UserSection }) => (
            <DailyActivityCard
                dayGroup={item.dayGroup}
                onOpenRejectDialog={rejectDialog.openDialog}
                onEditEntry={handleEditEntry}
                onTravelTap={setTravelEntry}
                isAdmin={isAdmin}
                showApprove={true}
                onAddEntry={
                    isAdmin ? () => handleAddEntry(section.userId, section.userName) : undefined
                }
                showConsolidate={isAdmin}
                onConsolidate={handleConsolidate}
            />
        ),
        [rejectDialog.openDialog, handleEditEntry, isAdmin, handleAddEntry, handleConsolidate]
    )

    const renderUserSectionHeader = useCallback(
        ({ section }: { section: UserSection }) => (
            <UserSectionHeader
                section={section}
                expanded={!!expandedUserKeys[section.userId]}
                onToggle={() => toggleUserKey(section.userId)}
            />
        ),
        [expandedUserKeys, toggleUserKey]
    )

    if (settingsLoading || reportingData.isLoading) {
        return <LoadingIndicator />
    }

    const listHeader = (
        <ListHeader
            periodLabel={reportingData.periodLabel}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onExport={exportToXLSX}
            isExporting={isExporting}
            hasEntries={reportingData.hasEntries}
            isPeriodLocked={isPeriodLocked}
            onLock={handleLockPeriod}
            onUnlock={() => unlockPeriod()}
            isLocking={isLocking}
            isUnlocking={isUnlocking}
            shouldOfferLock={shouldOfferLock}
            periodDate={periodDate}
            onDatePicked={handleDatePicked}
            groupingMode={groupingMode}
            onGroupingModeChange={setGroupingMode}
        />
    )

    const emptyComponent = reportingData.hasEntries ? null : (
        <View style={styles.emptyContainer}>
            <Text
                variant="bodyLarge"
                style={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            >
                No entries to review
            </Text>
            <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
                No entries found for this period
            </Text>
        </View>
    )

    const listFooter = reportingData.hasEntries ? (
        <PeriodTotal totalHours={periodTotalHours} />
    ) : null

    return (
        <ListWrapper>
            <EntriesSectionList
                groupingMode={groupingMode}
                daySections={daySections}
                userSections={userSections}
                renderDayItem={renderDayItem}
                renderDaySectionHeader={renderDaySectionHeader}
                renderUserItem={renderUserItem}
                renderUserSectionHeader={renderUserSectionHeader}
                listHeader={listHeader}
                listFooter={listFooter}
                emptyComponent={emptyComponent}
            />

            <RejectDialog
                visible={rejectDialog.isOpen}
                onDismiss={rejectDialog.closeDialog}
                onConfirm={rejectDialog.confirmReject}
                isLoading={rejectDialog.isLoading}
            />

            <ManualEntryDialog
                visible={!!editingEntry}
                orgSlug={params.orgSlug}
                onDismiss={() => setEditingEntry(null)}
                entry={editingEntry}
                isPrivilegedAction={isAdmin}
                targetUserId={editingEntry?.user}
                targetUserName={editingEntry?.expand?.user?.name}
                entriesLockedUntil={entriesLockedUntil}
            />

            <ManualEntryDialog
                visible={!!addingForUser}
                orgSlug={params.orgSlug}
                onDismiss={() => setAddingForUser(null)}
                isPrivilegedAction={isAdmin}
                targetUserId={addingForUser?.userId}
                targetUserName={addingForUser?.userName}
                entriesLockedUntil={entriesLockedUntil}
            />

            <ManualEntryDialog
                visible={!!consolidationData}
                orgSlug={params.orgSlug}
                onDismiss={() => {
                    if (consolidationData) {
                        const gk = `${consolidationData.userId}:${getISODate(consolidationData.clockIn.toISOString())}`
                        useConsolidationStore.getState().clearSelection(gk)
                    }
                    setConsolidationData(null)
                }}
                isPrivilegedAction
                targetUserId={consolidationData?.userId}
                targetUserName={consolidationData?.userName}
                entriesLockedUntil={entriesLockedUntil}
                prefill={consolidationData}
                entriesToDeleteOnSave={consolidationData?.entriesToDelete}
                consolidationNote={consolidationData?.consolidationNote}
                originalTotalSeconds={consolidationData?.originalTotalSeconds}
            />

            <TravelMapModal
                entry={travelEntry}
                orgSlug={params.orgSlug}
                userColor={travelEntry ? (userColorMap.get(travelEntry.user) ?? '') : ''}
                onDismiss={() => setTravelEntry(null)}
            />

            <Dialog visible={showLockConfirm} onDismiss={() => setShowLockConfirm(false)}>
                <Dialog.Title>Active Entries Found</Dialog.Title>
                <Dialog.Content>
                    <Text>
                        {activeConflicts.length} active{' '}
                        {activeConflicts.length === 1 ? 'entry' : 'entries'} will be clocked out and
                        set to pending before locking this period.
                    </Text>
                </Dialog.Content>
                <Dialog.Actions>
                    <Button
                        title="Cancel"
                        onPress={() => setShowLockConfirm(false)}
                        disabled={isLocking}
                    />
                    <Button
                        title="Close & Lock"
                        onPress={handleConfirmBulkCloseAndLock}
                        loading={isLocking}
                        disabled={isLocking}
                    />
                </Dialog.Actions>
            </Dialog>
        </ListWrapper>
    )
}

function SectionDivider({ trailingSection }: { trailingSection?: boolean }) {
    const theme = useTheme()
    if (trailingSection) return null
    return (
        <View style={[styles.sectionDivider, { backgroundColor: theme.colors.outlineVariant }]} />
    )
}

interface EntriesSectionListProps {
    groupingMode: GroupingMode
    daySections: DaySection[]
    userSections: UserSection[]
    renderDayItem: (info: { item: DayUserGroup }) => React.ReactElement
    renderDaySectionHeader: (info: { section: DaySection }) => React.ReactElement
    renderUserItem: (info: { item: UserDayGroup; section: UserSection }) => React.ReactElement
    renderUserSectionHeader: (info: { section: UserSection }) => React.ReactElement
    listHeader: React.ReactElement
    listFooter: React.ReactElement | null
    emptyComponent: React.ReactElement | null
}

function EntriesSectionList({
    groupingMode,
    daySections,
    userSections,
    renderDayItem,
    renderDaySectionHeader,
    renderUserItem,
    renderUserSectionHeader,
    listHeader,
    listFooter,
    emptyComponent,
}: EntriesSectionListProps) {
    if (groupingMode === 'day') {
        return (
            <SectionList<DayUserGroup, DaySection>
                sections={daySections}
                keyExtractor={item => `day-${item.userId}-${item.dayGroup.dateKey}`}
                renderSectionHeader={renderDaySectionHeader}
                renderItem={renderDayItem}
                ListHeaderComponent={listHeader}
                ListEmptyComponent={emptyComponent}
                ListFooterComponent={listFooter}
                SectionSeparatorComponent={SectionDivider}
                stickySectionHeadersEnabled={true}
                initialNumToRender={10}
                maxToRenderPerBatch={10}
                windowSize={5}
                contentContainerStyle={styles.listContent}
            />
        )
    }

    return (
        <SectionList<UserDayGroup, UserSection>
            sections={userSections}
            keyExtractor={(item, index) => `user-${item.dateKey}-${index}`}
            renderSectionHeader={renderUserSectionHeader}
            renderItem={renderUserItem}
            ListHeaderComponent={listHeader}
            ListEmptyComponent={emptyComponent}
            ListFooterComponent={listFooter}
            SectionSeparatorComponent={SectionDivider}
            stickySectionHeadersEnabled={true}
            initialNumToRender={10}
            maxToRenderPerBatch={10}
            windowSize={5}
            contentContainerStyle={styles.listContent}
        />
    )
}

interface UserSectionHeaderProps {
    section: UserSection
    expanded: boolean
    onToggle: () => void
}

function UserSectionHeader({ section, expanded, onToggle }: UserSectionHeaderProps) {
    const theme = useTheme()
    const semanticColors = useSemanticColors()

    return (
        <Pressable
            onPress={onToggle}
            style={[
                styles.sectionHeader,
                {
                    backgroundColor: theme.colors.surface,
                    borderBottomColor: theme.colors.outlineVariant,
                },
            ]}
        >
            {section.userColor ? (
                <View style={[styles.userColorChip, { backgroundColor: section.userColor }]} />
            ) : (
                <Icon source="account" size={18} color={theme.colors.onSurfaceVariant} />
            )}
            <Text
                variant="titleMedium"
                style={{ fontWeight: '600', color: theme.colors.onSurface, flex: 1 }}
            >
                {section.userName}
            </Text>
            {section.pendingCount > 0 && (
                <Text variant="bodySmall" style={{ color: semanticColors.statusPending }}>
                    {section.pendingCount} unreviewed
                </Text>
            )}
            <Text variant="bodySmall" style={{ color: theme.colors.primary, fontWeight: '600' }}>
                {formatHoursReadable(section.totalWorkHours)}
            </Text>
            <Icon
                source={expanded ? 'chevron-up' : 'chevron-down'}
                size={24}
                color={theme.colors.onSurfaceVariant}
            />
        </Pressable>
    )
}

interface DaySectionHeaderProps {
    section: DaySection
    expanded: boolean
    onToggle: () => void
}

function DaySectionHeader({ section, expanded, onToggle }: DaySectionHeaderProps) {
    const theme = useTheme()
    const semanticColors = useSemanticColors()

    return (
        <Pressable
            onPress={onToggle}
            style={[
                styles.sectionHeader,
                {
                    backgroundColor: theme.colors.surface,
                    borderBottomColor: theme.colors.outlineVariant,
                },
            ]}
        >
            <Text
                variant="titleMedium"
                style={{ fontWeight: '600', color: theme.colors.onSurface, flex: 1 }}
            >
                {section.dateLabel}
            </Text>
            {section.pendingCount > 0 && (
                <Text variant="bodySmall" style={{ color: semanticColors.statusPending }}>
                    {section.pendingCount} unreviewed
                </Text>
            )}
            <Text variant="bodySmall" style={{ color: theme.colors.primary, fontWeight: '600' }}>
                {formatHoursReadable(section.totalWorkHours)}
            </Text>
            <Icon
                source={expanded ? 'chevron-up' : 'chevron-down'}
                size={24}
                color={theme.colors.onSurfaceVariant}
            />
        </Pressable>
    )
}

interface ListHeaderProps {
    periodLabel: string
    onPrevious: () => void
    onNext: () => void
    onExport: () => void
    isExporting: boolean
    hasEntries: boolean
    isPeriodLocked: boolean
    onLock: () => void
    onUnlock: () => void
    isLocking: boolean
    isUnlocking: boolean
    shouldOfferLock: boolean
    periodDate: Date
    onDatePicked: (date: Date) => void
    groupingMode: GroupingMode
    onGroupingModeChange: (mode: GroupingMode) => void
}

function ListHeader({
    periodLabel,
    onPrevious,
    onNext,
    onExport,
    isExporting,
    hasEntries,
    isPeriodLocked,
    onLock,
    onUnlock,
    isLocking,
    isUnlocking,
    shouldOfferLock,
    periodDate,
    onDatePicked,
    groupingMode,
    onGroupingModeChange,
}: ListHeaderProps) {
    const { width } = useWindowDimensions()
    const isMobile = width < BREAKPOINTS.md

    const downloadButton = (
        <IconButton
            icon="download"
            size={20}
            onPress={onExport}
            disabled={!hasEntries || isExporting}
            loading={isExporting}
        />
    )

    const unlockButton = isPeriodLocked ? (
        <Button
            title="Unlock"
            onPress={onUnlock}
            loading={isUnlocking}
            disabled={isUnlocking}
            icon="lock-open-variant"
            compact
        />
    ) : null

    const periodNavigator = (
        <PeriodNavigator
            periodLabel={periodLabel}
            onPrevious={onPrevious}
            onNext={onNext}
            periodDate={periodDate}
            onDatePicked={onDatePicked}
        />
    )

    const groupingToggle = hasEntries ? (
        <View style={styles.groupingToggleWrapper}>
            <SegmentedButtons
                value={groupingMode}
                onValueChange={value => onGroupingModeChange(value as GroupingMode)}
                buttons={[
                    {
                        value: 'day',
                        label: 'Day',
                        icon: 'calendar-today',
                        showSelectedCheck: false,
                        style: styles.groupingToggleButton,
                    },
                    {
                        value: 'user',
                        label: 'User',
                        icon: 'account',
                        showSelectedCheck: false,
                        style: styles.groupingToggleButton,
                    },
                ]}
            />
        </View>
    ) : null

    return (
        <View>
            {isMobile ? (
                <>
                    <View style={styles.controlsRow}>
                        {downloadButton}
                        {unlockButton}
                        <View style={styles.spacer} />
                        {groupingToggle}
                    </View>
                    <View style={styles.periodNavigatorRow}>{periodNavigator}</View>
                </>
            ) : (
                <View style={styles.periodRow}>
                    {downloadButton}
                    {unlockButton}
                    <View style={styles.periodNavigatorWrapper}>{periodNavigator}</View>
                    {groupingToggle}
                </View>
            )}
            <LockedBanner isVisible={isPeriodLocked} />
            <LockSuggestionPanel
                isVisible={shouldOfferLock}
                onLock={onLock}
                isLocking={isLocking}
            />
        </View>
    )
}

function LockSuggestionPanel({
    isVisible,
    onLock,
    isLocking,
}: {
    isVisible: boolean
    onLock: () => void
    isLocking: boolean
}) {
    const theme = useTheme()

    if (!isVisible) return null

    return (
        <View style={[styles.lockSuggestion, { backgroundColor: theme.colors.secondaryContainer }]}>
            <View style={styles.lockSuggestionContent}>
                <Icon source="check-circle" size={20} color={theme.colors.onSecondaryContainer} />
                <Text
                    variant="bodySmall"
                    style={{ color: theme.colors.onSecondaryContainer, flex: 1 }}
                >
                    All entries have been reviewed. Lock this period to prevent further changes?
                </Text>
            </View>
            <Button
                title="Lock Period"
                onPress={onLock}
                loading={isLocking}
                disabled={isLocking}
                icon="lock"
                compact
            />
        </View>
    )
}

function LockedBanner({ isVisible }: { isVisible: boolean }) {
    const theme = useTheme()

    if (!isVisible) return null

    return (
        <View style={[styles.lockedBanner, { backgroundColor: theme.colors.surfaceVariant }]}>
            <Icon source="lock" size={16} color={theme.colors.onSurfaceVariant} />
            <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
                This period is locked. Workforce users cannot modify entries.
            </Text>
        </View>
    )
}

const styles = StyleSheet.create({
    listContent: {
        paddingBottom: 16,
        maxWidth: 1000,
        width: '100%',
        alignSelf: 'center',
    },
    periodRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
    },
    periodNavigatorWrapper: {
        flex: 1,
    },
    controlsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 8,
    },
    spacer: {
        flex: 1,
    },
    periodNavigatorRow: {
        paddingHorizontal: 16,
        paddingTop: 4,
    },
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 10,
        paddingHorizontal: 16,
        borderBottomWidth: 1,
        gap: 8,
    },
    userColorChip: {
        width: 8,
        height: 22,
        borderRadius: 2,
    },
    groupingToggleWrapper: {
        marginHorizontal: 4,
        minWidth: 200,
    },
    groupingToggleButton: {
        minWidth: 96,
    },
    sectionDivider: {
        height: 8,
    },
    emptyContainer: {
        padding: 32,
        alignItems: 'center',
        gap: 8,
    },
    lockedBanner: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingHorizontal: 16,
        paddingVertical: 10,
    },
    lockSuggestion: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        marginHorizontal: 16,
        marginTop: 8,
        marginBottom: 8,
        paddingHorizontal: 16,
        paddingVertical: 12,
        borderRadius: 12,
    },
    lockSuggestionContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        flex: 1,
    },
})
